<template>
  <div
    class="kl-activity"
    :class="{ open: expanded, highlighted: highlighted }"
  >
    <div class="kl-act-head" @click="expanded = !expanded">
      <!-- Único punto de agarre del arrastre: el resto de la cabecera sigue
           sirviendo para expandir la actividad. -->
      <span v-if="allows_crud" class="handle kl-act-handle" @click.stop>
        <b-icon icon="arrows-expand" scale="0.95"></b-icon>
      </span>
      <span class="kl-caret">&#9656;</span>
      <div class="kl-act-card">
        <div class="kl-act-top">
          <!-- E24: una evaluacion referenciada lleva su tipo adelante del nombre.
               Es lo unico que distingue una tarjeta de la otra a simple vista, y
               marca que su contenido se lee del Programa y no se edita aqui. -->
          <span
            v-if="cardTypeLabel"
            class="kl-chip kl-chip-eval"
            :title="
              $t('teaching.instructional.referencedEvaluationTooltip', {
                evaluation: evaluationEntityName,
                program: programEntityName,
              })
            "
          >
            {{ cardTypeLabel }}
          </span>
          <span class="kl-act-title">
            {{ cardTitle }}
          </span>
          <!-- Los RA que trabaja la actividad, a la derecha del título. -->
          <span class="kl-act-units">
            <span
              v-for="study_unit in activityStudyUnits"
              :key="study_unit.id"
              class="kl-chip"
              :title="study_unit.name"
            >
              {{ $t("reusable.common.numberAbbreviation")
              }}{{ getCompetenceUnitOrder(study_unit.temp_competence_unit)
              }}{{ study_unit.order }}
            </span>
          </span>
          <span class="ml-auto">
            <InstructionalStatusBadge
              :status="activity.instructional_status"
              class="mr-2"
            ></InstructionalStatusBadge>
            <span v-if="allows_crud" @click.stop>
              <!-- Bibliografía de la actividad: se elige del catálogo del
                   Programa, igual que en las evaluaciones de la Asignatura. -->
              <ButtonSmall
                :tooltip_text="
                  $t(
                    'matterProgram.matterProgramView.editBibliographicResources'
                  )
                "
                :click_button="
                  () =>
                    $bvModal.show(
                      `instructional-activity-bibliography-modal-${activity.id}`
                    )
                "
                :icon="'book'"
              ></ButtonSmall>
              <!-- E45 · Regenerar esta pieza con IA sin pasar por el modal de creación
                   rápida: ese es un flujo de creación y obliga a buscarla entre todas las
                   del diseño, cuando la acción es de una sola tarjeta.
                   Va dentro del grupo de acciones y no suelto a la vista: reemplaza textos
                   que alguien pudo escribir a mano y no hay cómo deshacerlo. -->
              <ButtonSmall
                v-if="shows_ai"
                :tooltip_text="
                  $t('reusable.aiInstructionalBuilderModal.redoEntity', {
                    entity: pieceEntityName,
                  })
                "
                :click_button="openRedo"
              >
                <!-- El mismo icono con el que se rehace el resumen del diseño: es el
                     mismo gesto sobre otra cosa. Va por el slot porque `icon` no lo
                     conoce, y el svg de IA no sirve acá: es blanco, pensado para el botón
                     de fondo coloreado, y sobre uno transparente no se ve. -->
                <template #icon>
                  <b-icon icon="arrow-repeat"></b-icon>
                </template>
              </ButtonSmall>
              <ButtonSmall
                :tooltip_text="
                  $t('reusable.common.editTooltip', {
                    entity: pieceEntityName,
                  })
                "
                :click_button="
                  () =>
                    $bvModal.show(
                      `instructional-activity-edit-modal-${activity.id}`
                    )
                "
                :icon="'square'"
              ></ButtonSmall>
              <!-- E24: el basurero es SOLO de las Actividades del diseño, donde
                   elimina la actividad y su evaluacion (E16). Una evaluacion
                   referenciada la creo el Programa y ahi sigue viviendo, asi que
                   desde aca no se elimina: para sacarla de la secuencia se la
                   arrastra al bloque de sueltas, que es el gesto inverso al que
                   la ubico. Un basurero sobre ella prometeria otra cosa. -->
              <ButtonSmall
                v-if="!referencedEvaluation"
                :tooltip_text="$t('reusable.common.delete')"
                :click_button="askDelete"
                :icon="'trash'"
              ></ButtonSmall>
            </span>
          </span>
        </div>
        <div class="kl-act-meta">
          <div v-if="activityMoments.length > 0" class="kl-meta-group">
            <span
              v-for="moment in activityMoments"
              :key="`moment-${moment.id}`"
              class="kl-moment-pill"
              :style="momentPillStyle(moment)"
              :title="moment.description || moment.name"
            >
              {{ moment.name }}
            </span>
          </div>
          <!-- La semana no se guarda: sale de acumular las horas de aula en el
               orden del diseño. Por eso una actividad larga puede abarcar dos. -->
          <div v-if="weekLabel" class="kl-meta-group">
            <b-icon icon="calendar3" scale="0.85"></b-icon>
            <span
              class="kl-meta-value"
              :class="{ 'kl-week-overflow': activityWeek.overflow }"
              v-b-tooltip.v-secondary.noninteractive="weekTooltip"
            >
              {{ weekLabel }}
            </span>
          </div>
          <!-- Sin semana se dice por qué: el campo vacío no distinguía "no
               corresponde" de "falta cargar algo". -->
          <div v-else-if="weekMissingLabel" class="kl-meta-group">
            <b-icon icon="calendar3" scale="0.85"></b-icon>
            <span class="kl-meta-empty">{{ weekMissingLabel }}</span>
          </div>

          <div v-if="cardHours" class="kl-meta-group">
            <b-icon icon="clock" scale="0.85"></b-icon>
            <span class="kl-meta-value">
              {{ cardHours }}
              {{
                $t("teaching.instructional.activityHoursField").toLowerCase()
              }}
            </span>
          </div>
          <div v-if="activity.autonomous_work_hours" class="kl-meta-group">
            <b-icon icon="house" scale="0.85"></b-icon>
            <span class="kl-meta-value">
              {{ autonomousHours }}
              {{
                $t(
                  "teaching.instructional.activityAutonomousHoursField"
                ).toLowerCase()
              }}
            </span>
          </div>
          <div v-if="participationLabel" class="kl-meta-group">
            <b-icon icon="people" scale="0.85"></b-icon>
            <span class="kl-meta-value">{{ participationLabel }}</span>
          </div>
          <div v-if="cardWeighing" class="kl-meta-group">
            <span class="kl-meta-value kl-weighing-value">
              {{ cardWeighing }}%
            </span>
          </div>
        </div>
      </div>
    </div>

    <!-- `v-show` y no `v-if`: al imprimir hace falta que el cuerpo YA esté en el DOM. El
         evento `beforeprint` es síncrono y Vue renderiza en el microtask siguiente, así
         que una tarjeta cerrada no llegaba a abrirse antes de que el navegador arme la
         hoja. Dejándolo montado y oculto, el `@media print` lo muestra sin depender de
         ningún re-render. -->
    <div v-show="expanded" class="kl-act-body">
      <!-- E24: se dice de donde sale lo que se esta leyendo. Sin esto la tarjeta
           parece editable y no lo es: el nombre, los IL, las horas y la
           ponderacion son del Programa. -->
      <p v-if="referencedEvaluation" class="kl-act-origin">
        <b-icon icon="link45deg" scale="0.9" class="mr-1"></b-icon>
        {{
          $t("teaching.instructional.referencedEvaluationOrigin", {
            evaluation: evaluationEntityName,
            program: programEntityName,
          })
        }}
      </p>
      <div class="kl-act-panels">
        <div class="kl-act-main">
          <!-- E50 · De qué trata la pieza. Es el primer bloque del cuerpo porque es el
               resumen: dice qué es esto antes de que la tarjeta se ponga a explicar cómo
               se hace. La IA ya lo escribía y el formulario del Programa ya lo editaba —
               sólo la tarjeta lo callaba, y por eso había que abrir el formulario para
               saber de qué era una pieza.
               Es el texto escrito para quien DISEÑA, no para el estudiante: ese otro es
               `description_for_student`, el bloque que sigue. -->
          <section
            v-if="activityDescription || !referencedEvaluation"
            class="kl-block"
          >
            <h6 class="kl-act-subtitle">
              {{ $t("reusable.common.description") }}
            </h6>
            <RenderVHtml
              v-if="activityDescription"
              :content="activityDescription"
            ></RenderVHtml>
            <span v-else class="text-muted small">
              {{ $t("teaching.instructional.activityNoDescription") }}
            </span>
          </section>
          <!-- El "sin instrucciones" sólo se muestra donde el usuario PUEDE
               completarlo. En una Actividad del diseño es una invitación a llenarlo y
               el formulario tiene el campo; en una evaluación referenciada el texto es
               del Programa y desde acá no se edita, así que el hueco invitaba a una
               acción que no existe — y dejaba la tarjeta diciendo que falta algo que
               el formulario ni siquiera ofrece. -->
          <section
            v-if="studentInstructions || !referencedEvaluation"
            class="kl-block"
          >
            <h6 class="kl-act-subtitle">
              {{ $t("teaching.instructional.activityStudentInstructions") }}
            </h6>
            <RenderVHtml
              v-if="studentInstructions"
              :content="studentInstructions"
            ></RenderVHtml>
            <span v-else class="text-muted small">
              {{ $t("teaching.instructional.activityNoInstructions") }}
            </span>
          </section>
          <section v-if="activity.professor_instructions" class="kl-block">
            <h6 class="kl-act-subtitle">
              {{ $t("teaching.instructional.activityProfessorInstructions") }}
            </h6>
            <RenderVHtml
              :content="activity.professor_instructions"
            ></RenderVHtml>
          </section>
          <section v-if="activityContents.length > 0" class="kl-block">
            <h6 class="kl-act-subtitle">
              {{ $t("teaching.instructional.activityContents") }}
            </h6>
            <div
              v-for="content in activityContents"
              :key="`content-${content.id}`"
              class="kl-content-row"
            >
              <RenderVHtml :content="content.content"></RenderVHtml>
            </div>
          </section>

          <section v-if="activityIndicators.length > 0" class="kl-block">
            <h6 class="kl-act-subtitle">
              {{
                $getVisibleNames(
                  "teaching.evaluationcriteriamicro",
                  true,
                  "Indicadores de Logro"
                )
              }}
            </h6>
            <div class="kl-il-list">
              <SentenceContainer
                v-for="indicator in activityIndicators"
                :key="indicator.id"
                :unique_id="`activity-${activity.id}-il-${indicator.id}`"
                :showColorCognitiveLevel="true"
                :Sentence="indicator"
                :boundedTextContainer="true"
                :single_line="true"
              >
                <template v-slot:order>
                  <span>
                    {{ indicatorPrefix(indicator) }}{{ indicator.order }}
                  </span>
                </template>
              </SentenceContainer>
            </div>
          </section>
        </div>

        <aside class="kl-act-side">
          <section v-if="activityStrategies.length > 0" class="kl-block">
            <h6 class="kl-act-subtitle">
              {{
                $getVisibleNames(
                  "mesh.methodologicalstrategy",
                  true,
                  "Estrategias Metodológicas"
                )
              }}
            </h6>
            <span
              v-for="strategy in activityStrategies"
              :key="`strategy-${strategy.id}`"
              class="kl-chip"
              :title="strategyTooltip(strategy)"
            >
              {{ strategy.name }}
            </span>
          </section>
          <!-- E36 · Qué produce el estudiante. Va inmediatamente después de las
               Estrategias: una dice cómo se trabaja y la otra qué queda de ese trabajo,
               y son el par que la tabla de cobertura enfrenta. -->
          <section v-if="activityEvidences.length > 0" class="kl-block">
            <h6 class="kl-act-subtitle">
              {{
                $getVisibleNames(
                  "manual.evidencia_de_desempeño",
                  true,
                  "Evidencias de Desempeño"
                )
              }}
            </h6>
            <span
              v-for="evidence in activityEvidences"
              :key="`evidence-${evidence.id}`"
              class="kl-chip"
              :title="evidenceTooltip(evidence)"
            >
              {{ evidence.name }}
            </span>
          </section>
          <section v-if="activityStudyEnvironments.length > 0" class="kl-block">
            <h6 class="kl-act-subtitle">
              {{
                $getVisibleNames(
                  "mesh.studyenvironment",
                  true,
                  "Ambientes de Aprendizaje"
                )
              }}
            </h6>
            <span
              v-for="environment in activityStudyEnvironments"
              :key="`environment-${environment.id}`"
              class="kl-chip"
              :title="environment.description"
            >
              {{ environment.name }}
            </span>
          </section>
          <section v-if="activitySupportResources.length > 0" class="kl-block">
            <h6 class="kl-act-subtitle">
              {{ $t("matterProgram.matterProgramView.supportResources") }}
            </h6>
            <div
              v-for="group in activitySupportResources"
              :key="`support-type-${group.type}`"
              class="kl-side-row"
            >
              <span class="kl-support-type">{{ group.type_name }}:</span>
              <!-- El recurso se escribe en la Asignatura con el editor, así que es texto
                   enriquecido y puede traer un enlace. Aplanarlo con `$htmlToPlainText`
                   dejaba el enlace como texto suelto: se veía la dirección y no se podía
                   abrir. Va por el mismo sink sanitizado que la bibliografía. -->
              <span
                v-for="(item, index) in group.items"
                :key="`support-${group.type}-${index}`"
                class="kl-support-item"
              >
                <RenderVHtml :content="item"></RenderVHtml
                ><span v-if="index < group.items.length - 1">, </span>
              </span>
            </div>
          </section>
          <section
            v-if="activityBibliographicResources.length > 0"
            class="kl-block"
          >
            <h6 class="kl-act-subtitle">
              {{ $t("matterProgram.matterProgramView.bibliographicResources") }}
            </h6>
            <div
              v-for="resource in activityBibliographicResources"
              :key="`biblio-${resource.id}`"
              class="kl-side-row"
            >
              <!-- E37 · Se fue el distintivo "Programa". Existía porque había DOS listas de
                   bibliografía —la de la ficha y la que declaraba la evaluación (E25.1)— y
                   había que decir cuál no se podía desvincular desde acá. Con una sola fila
                   toda la bibliografía es del wrapper y el rótulo diría algo que ya no
                   distingue nada. -->
              <RenderVHtml :content="resource.description"></RenderVHtml>
            </div>
          </section>
        </aside>
      </div>

      <!-- E41 · Orientación. Va a ancho completo y DEBAJO de las dos columnas porque no es
           un dato más de la pieza: es el párrafo que le explica al estudiante por qué esto
           viene aquí y qué le habilita después. Las instrucciones dicen qué hacer; ésta dice
           para qué, y se lee con la pieza entera a la vista, no al lado de una de sus mitades. -->
      <section v-if="orientation || allows_crud" class="kl-block kl-act-orientation">
        <h6 class="kl-act-subtitle">
          {{ $t("teaching.instructional.activityOrientation") }}
        </h6>
        <!-- El índice: módulo, momento y denominación de la pieza. No se guarda — se arma con
             los tres datos que ya la ubican — y es lo que en el DI de referencia encabeza la
             orientación para decir en qué punto del recorrido está parado el que lee. -->
        <p v-if="indexLabel" class="kl-act-index">{{ indexLabel }}</p>
        <div
          v-if="allows_crud"
          class="kl-orientation-box"
          @click="$bvModal.show(orientationModalId)"
        >
          <RenderVHtml
            v-if="orientation"
            v-b-tooltip.v-secondary.noninteractive.hover="{
              title: $t('teaching.instructional.activityOrientationEdit'),
              boundary: 'viewport',
            }"
            :content="orientation"
          ></RenderVHtml>
          <span v-else class="kl-act-empty">
            {{ $t("teaching.instructional.activityOrientationEmpty") }}
          </span>
        </div>
        <RenderVHtml v-else :content="orientation"></RenderVHtml>
        <ContainerRichTextKlv3
          v-if="allows_crud"
          :id_modal="orientationModalId"
          :title_modal="$t('teaching.instructional.activityOrientation')"
          :size_modal="`lg`"
          :content_rich_text="orientation"
          :permit_blank="true"
          @save="saveOrientation"
        ></ContainerRichTextKlv3>
      </section>

      <!-- Los archivos que acompañan a la pieza. Van debajo de la orientación porque son
           material de apoyo para leerla, no un dato de la ficha; y son los MISMOS que se
           ven en la evaluación, no una copia: se cargan allá, acá sólo se muestran. -->
      <section
        v-if="evaluationFiles.length > 0 || allows_crud"
        class="kl-block"
      >
        <h6 class="kl-act-subtitle">
          {{ $t("teaching.instructional.activityAttachedFiles") }}
          <ButtonSmall
            v-if="allows_crud"
            :tooltip_text="$t('evaluations.newEvaluations.addAttachedFile')"
            :click_button="() => $bvModal.show(filesModalId)"
            :custom_class="`ml-1`"
            :icon="'plus'"
          ></ButtonSmall>
        </h6>
        <FileContainer
          :Files="evaluationFiles"
          :allows_crud="allows_crud"
        ></FileContainer>
        <p v-if="evaluationFiles.length === 0" class="kl-act-empty mb-0">
          {{ $t("teaching.instructional.activityAttachedFilesEmpty") }}
        </p>
      </section>

      <b-modal
        v-if="allows_crud"
        :id="filesModalId"
        :title="$t('evaluations.newEvaluations.selectFiles')"
        ignore-enforce-focus-selector="#sidebar-right"
        hide-footer
      >
        <FileSelector @created="attachFile"></FileSelector>
      </b-modal>

      <!-- Descriptores de la pieza. Sólo en las Actividades: la Actividad es lo que el
           diseñador redacta, y sus campos son fijos —nombre, instrucciones, orientación,
           archivos—. Cuando le falta uno (un video, un enlace, otro párrafo) agrega un
           Descriptor y le pone el título que quiera, en vez de pedir una columna nueva.
           El catálogo de títulos es el mismo del Programa y del Diseño; lo que separa a
           estos de aquéllos es el `page_position` del título, que para la pieza es 4. -->
      <section v-if="isActivity" class="kl-block kl-act-descriptors">
        <!-- Mismo encabezado que los Descriptores del Programa y del Diseno:
             `ProgramView` es el que pone la fila en flex —titulo al centro, iconos a la
             derecha—. -->
        <DescriptorsContainer
          :ref_component="`InstructionalActivity-${activity.id}-`"
          :ProgramView="true"
          :warn_shared_title="false"
          :Title_text="$t('teaching.instructional.activityDescriptorsTitle')"
          :Text_info_tooltip="$t('teaching.instructional.activityDescriptorsHint')"
          :Title="activityDescriptorTitles"
          :Descriptors="activityDescriptors"
          :allows_crud="allows_crud"
          :Order="1"
          :VisibleOrder="false"
          :PagePosition="PIECE_DESCRIPTOR_PAGE_POSITION"
          :can_update="allows_crud"
          :can_delete="allows_crud"
          :can_delete_title="false"
          :show_is_default="false"
          @updateOrder="updateOrderActivityDescriptors"
          @updateState="updateVisibleActivityDescriptor"
          @createDescription="createActivityDescriptor"
          @updateDescription="updateActivityDescriptor"
          @createTitle="createActivityDescriptorTitle"
          @updateTitle="updateActivityDescriptorTitle"
          @deleteDescriptors="deleteActivityDescriptor"
        ></DescriptorsContainer>
      </section>

      <div class="text-center mt-2">
        <!-- El botón al instrumento es sólo de las evaluaciones: la Actividad no se rinde
             ni se califica, así que no hay instrumento que abrir. Lo que la Actividad
             necesita para crecer son sus Descriptores, acá arriba. -->
        <ButtonMedium
          v-if="activity.evaluation && !isActivity"
          class="mr-2"
          :variant="'primary'"
          :text_button="
            $t('teaching.instructional.activityGoTo', {
              entity: goToEntityName,
            })
          "
          :tooltip_text="
            $t('teaching.instructional.activityGoToTooltip', {
              entity: goToEntityName,
            })
          "
          :click_button="redirectToInstrument"
          :icon="'card'"
        ></ButtonMedium>
      </div>
    </div>

    <b-modal
      :key="`activity-edit-${activity.id}`"
      :id="`instructional-activity-edit-modal-${activity.id}`"
      :title="
        $t('reusable.common.editModalTitle', {
          entity: $t('teaching.instructional.title'),
        })
      "
      size="xl"
      hide-footer
      ignore-enforce-focus-selector="#sidebar-right"
      @show="form_key++"
    >
      <!-- E38 · Se monta el MISMO formulario del Programa de Asignatura, no uno propio del
           diseño. Desde E37 la pieza es la evaluación: tener dos formularios para la misma fila
           obligaba a salir al Programa para tocar las horas y volver acá a ver el cambio.
           Los campos exclusivos del diseño ya viven dentro de ese formulario, en la sección
           plegable que arma `EvaluationInstructionalFields`. -->
      <MatterProgramEvaluationForm
        v-if="level_key === 'matter'"
        :key="form_key"
        :Evaluation="activity"
        :matter_id="activity.matter"
        :EvaluationSituations="evaluation_situations"
        :MatEvaStuEnvTimAll="time_allocation_rows"
        :evaluation_list="evaluation_list"
        @created_support_resource="$emit('resources-changed')"
        @updated="onEvaluationUpdated"
      ></MatterProgramEvaluationForm>
      <EPMatterProgramEvaluationForm
        v-else
        :key="form_key"
        :Evaluation="activity"
        :egress_profile_matter_id="activity.egress_profile_matter"
        :EvaluationSituations="evaluation_situations"
        :EPMatEvaStuEnvTimAll="time_allocation_rows"
        :evaluation_list="evaluation_list"
        @created_support_resource="$emit('resources-changed')"
        @updated="onEvaluationUpdated"
      ></EPMatterProgramEvaluationForm>
    </b-modal>

    <b-modal
      v-if="allows_crud"
      :key="`activity-bibliography-${activity.id}`"
      :id="`instructional-activity-bibliography-modal-${activity.id}`"
      :title="$t('matterProgram.matterProgramView.editBibliographicResources')"
      size="xl"
      hide-footer
      ignore-enforce-focus-selector="#sidebar-right"
      @hidden="refreshPiece"
    >
      <!-- E37 · Se reutiliza el componente del Programa en vez del propio del diseño.
           No es sólo por no duplicar: la bibliografía del wrapper es un M2M **con tabla
           intermedia** (`MatterEvaluationBibliographicResource`, que aporta `details` e
           `is_mandatory`), y DRF marca esos campos como de sólo lectura — un PATCH con la
           lista de ids, que es lo que hacía `ActivityBibliographicResource`, no escribe
           nada. El componente del Programa escribe la tabla intermedia, que es lo correcto. -->
      <MEvaluationBibliographicResource
        v-if="level_key === 'matter'"
        :m_evaluation_id="activity.id"
        :matter_id="activity.matter"
        :allows_crud="allows_crud"
        :can_delete="allows_crud"
        :can_update="allows_crud"
      ></MEvaluationBibliographicResource>
      <EPMEvaluationBibliographicResource
        v-else-if="egressProfileMatter"
        :epm_evaluation_id="activity.id"
        :ep_matter="egressProfileMatter"
        :allows_crud="allows_crud"
        :can_delete="allows_crud"
        :can_update="allows_crud"
      ></EPMEvaluationBibliographicResource>
    </b-modal>

    <!-- E45 · Regenerar con IA: el tema es el mismo campo del paso 1 del builder, y las
         tres casillas son las partes que pueden moverse por separado sin dejar la pieza
         contradiciéndose. La agrupación está explicada en `redoPieceWithAi`. -->
    <b-modal
      v-if="shows_ai"
      :key="`activity-redo-${activity.id}`"
      :id="redoModalId"
      :title="
        $t('reusable.aiInstructionalBuilderModal.redoEntity', {
          entity: pieceEntityName,
        })
      "
      size="lg"
      ignore-enforce-focus-selector="#sidebar-right"
      :no-close-on-backdrop="redoing"
      :no-close-on-esc="redoing"
      hide-footer
    >
      <RedoPieceFields
        :parts.sync="redo_parts"
        :theme.sync="redo_theme"
        :entity_name="pieceEntityName"
        :disabled="redoing"
        @submit="redoWithAi"
      ></RedoPieceFields>

      <div class="text-right">
        <ButtonMedium
          :variant="'primary'"
          :text_button="
            redoing
              ? $t('reusable.aiInstructionalBuilderModal.redoRunning')
              : $t('reusable.aiInstructionalBuilderModal.redo')
          "
          :tooltip_text="
            $t('reusable.aiInstructionalBuilderModal.redoEntity', {
              entity: pieceEntityName,
            })
          "
          :disabled_button="redoing || redo_parts.length === 0"
          :click_button="redoWithAi"
        ></ButtonMedium>
      </div>
    </b-modal>
  </div>
</template>

<script>
import * as names from "@/store/names";
import {
  ensureEvaluation,
  patchEvaluation,
  redoPieceWithAi,
  saveTests,
} from "@/components/teaching/Instructional/createDesignEvaluation";
import { mapGetters } from "vuex";
import { toast } from "@/utils/utils";
// E37 · El tipo 4 es la "Actividad" del Programa, que es la pieza propia del diseño.
import { WRAPPER_TYPE_ACTIVITY } from "@/utils/instructional_activity_types";

export default {
  name: "ActivityCard",
  components: {
    RenderVHtml: () => import("@/components/reusable/RenderVHtml"),
    SentenceContainer: () => import("@/components/reusable/SentenceContainer"),
    InstructionalStatusBadge: () => import("./InstructionalStatusBadge"),
    MatterProgramEvaluationForm: () =>
      import(
        "@/components/teaching/MatterProgram/MatterProgramEvaluation/MatterProgramEvaluationForm"
      ),
    EPMatterProgramEvaluationForm: () =>
      import(
        "@/components/teaching/MatterProgram/EPMatterProgramEvaluation/EPMatterProgramEvaluationForm"
      ),
    MEvaluationBibliographicResource: () =>
      import(
        "@/components/teaching/MatterProgram/MatterProgramEvaluation/MEvaluationBibliographicResource"
      ),
    EPMEvaluationBibliographicResource: () =>
      import(
        "@/components/teaching/MatterProgram/EPMatterProgramEvaluation/EPMEvaluationBibliographicResource"
      ),
    ContainerRichTextKlv3: () =>
      import("@/components/reusable/ContainerRichTextKlv3"),
    RedoPieceFields: () =>
      import("@/components/reusable/ai-builder/RedoPieceFields"),
    DescriptorsContainer: () =>
      import("@/components/reusable/DescriptorsContainer"),
    ButtonSmall: () =>
      import("@/components/reusable/Buttons/Small/ButtonSmall"),
    FileContainer: () => import("@/components/reusable/FileContainer"),
    FileSelector: () => import("@/components/reusable/FileSelector"),
    ButtonMedium: () =>
      import("@/components/reusable/Buttons/Medium/ButtonMedium"),
  },
  props: {
    activity: {
      type: Object,
      required: true,
    },
    instructional_design_id: {
      type: Number,
      required: true,
    },
    study_units: {
      type: Array,
      default: () => [],
    },
    bibliographic_resources: {
      type: Array,
      default: () => [],
    },
    teaching_support_resources: {
      type: Array,
      default: () => [],
    },
    activity_weeks: {
      type: Map,
      default: () => new Map(),
    },
    highlight_activity_id: {
      type: Number,
      default: null,
    },
    // E38 · Lo que el formulario del Programa pide como props.
    time_allocation_rows: {
      type: Array,
      default: () => [],
    },
    evaluation_situations: {
      type: Array,
      default: () => [],
    },
    // Las demás piezas del diseño: el formulario las usa para el tope del 100%.
    evaluation_list: {
      type: Array,
      default: () => [],
    },
    // E37 · Qué wrapper es la pieza: decide de dónde se leen las horas y a qué endpoint
    // van el PATCH y el DELETE.
    level_key: {
      type: String,
      default: "egress_profile_matter",
    },
    allows_crud: {
      type: Boolean,
      default: false,
    },
    // E45 · El gating de la creación rápida con IA, resuelto por el orquestador —flag
    // institucional, `ai_component`, grupo directivo y asignatura cerrada— y bajado por
    // props. Recalcularlo acá sería una segunda definición de la misma regla.
    shows_ai: {
      type: Boolean,
      default: false,
    },
    // E45 · La Asignatura del Perfil de Egreso en la que trabaja el diseño. El builder la
    // pide siempre, en los dos niveles, y la tarjeta no la puede deducir: en el nivel
    // "matter" su `activity.egress_profile_matter` viene vacío.
    egress_profile_matter_id: {
      type: Number,
      default: null,
    },
  },
  data() {
    return {
      // E37 · La constante se expone al componente para poder usarla en los computeds.
      WRAPPER_TYPE_ACTIVITY: WRAPPER_TYPE_ACTIVITY,
      expanded: false,
      form_key: 0,
      highlighted: false,
      highlight_timeout: null,
      // E45 · Lo que el modal de regenerar tiene elegido en este momento.
      redo_theme: "",
      redo_parts: [],
      redoing: false,
    };
  },
  computed: {
    evaluationEntityNamePlural() {
      return this.$getVisibleNames(
        "evaluations2.evaluation",
        true,
        "Evaluaciones"
      );
    },
    matterEntityName() {
      return this.$getVisibleNames(
        "mesh.egressprofilematter",
        false,
        "Asignatura"
      );
    },
    piecesEntityNamePlural() {
      return `${this.$getVisibleNames(
        "manual.instructional_design.activity",
        true,
        "Actividades"
      )}/${this.$getVisibleNames(
        "evaluations2.evaluation",
        true,
        "Evaluaciones"
      )}`;
    },
    evaluationEntityName() {
      return this.$getVisibleNames(
        "evaluations2.evaluation",
        false,
        "Evaluación"
      );
    },
    programEntityName() {
      return this.$getVisibleNames(
        "manual.matter_program",
        false,
        "Programa de Asignatura"
      );
    },
    ...mapGetters({
      competence_units: names.COMPETENCE_UNITS,
      instructional_designs: names.INSTRUCTIONAL_DESIGNS,
      instructional_methods: names.INSTRUCTIONAL_METHODS,
      evaluationCriteriasMicros: names.EVALUATION_CRITERIAS,
      study_unit_contents: names.CONTENTS,
      methodologicalStrategies: names.METHODOLOGICAL_STRATEGIES,
      procedureEvidences: names.PROCEDURE_EVIDENCES,
      evaluationTypes: names.EPM_EVALUATION_TYPES,
      egress_profiles_matters: names.EGRESS_PROFILES_MATTERS,
      studyEnvironments: names.STUDY_ENVIRONMENTS,
      instructional_modules: names.INSTRUCTIONAL_MODULES,
      new_evaluations: names.NEW_EVALUATIONS,
      matter_time_allocations: names.MATTER_TIME_ALLOCATIONS,
      ep_matter_time_allocations: names.EP_MATTER_TIME_ALLOCATIONS,
      descriptor_titles: names.EGRESS_PROFILE_MATTER_DESCRIPTORS,
      activity_descriptors: names.INSTRUCTIONAL_ACTIVITY_DESCRIPTORS,
    }),
    activityEntityName() {
      return this.$getVisibleNames(
        "manual.instructional_design.activity",
        false,
        "Actividad"
      );
    },
    // E37 · Los RA se derivan de los IL: el wrapper no guarda `study_units`.
    activityStudyUnits() {
      const study_unit_ids = new Set(
        this.activityIndicators.map((x) => x.study_unit)
      );
      return this.study_units
        .filter((x) => study_unit_ids.has(x.id))
        .sort((a, b) => a.order - b.order);
    },
    // E37 · La pieza ES la evaluación del Programa, así que ya no hay dos clases de tarjeta.
    // Lo que queda es el TIPO: la que el DI llama "Actividad" es la del tipo 4, y es la única
    // cuyo nombre, instrucciones e IL le corresponde editar al diseñador. Para las otras cuatro
    // —Diagnóstica, Formativa, Sumativa, Transversal— esos datos son del Programa y acá se
    // leen, igual que antes hacía `referencedEvaluation`.
    isActivity() {
      return this.activity.type == this.WRAPPER_TYPE_ACTIVITY;
    },
    // Se conserva el nombre para no reescribir el template entero: sigue significando
    // "esta pieza la define el Programa y el DI sólo la ubica".
    referencedEvaluation() {
      return !this.isActivity;
    },
    // El tipo, delante del nombre. Antes sólo lo llevaban las referenciadas; ahora lo lleva
    // toda pieza que no sea una Actividad, que es la misma regla dicha al derecho.
    cardTypeLabel() {
      if (this.isActivity) return null;
      const type = this.evaluationTypes.find((x) => x.id == this.activity.type);
      return type ? type.value : null;
    },
    // La posición de los títulos que son de la PIEZA. El Programa usa la 1 y la 2, el
    // Diseño la 3, y la pieza la 4; todos salen del mismo catálogo de la institución.
    PIECE_DESCRIPTOR_PAGE_POSITION() {
      return 4;
    },
    // El ancla de los bloques de ESTA pieza. La pieza es la evaluación del Programa, y ésa
    // vive en dos modelos según el nivel de la asignatura: se manda uno de los dos campos.
    descriptorAnchor() {
      return this.level_key === "matter"
        ? { matter_evaluation: this.activity.id }
        : { egress_profile_matter_evaluation: this.activity.id };
    },
    activityDescriptors() {
      const field =
        this.level_key === "matter"
          ? "matter_evaluation"
          : "egress_profile_matter_evaluation";
      return this.activity_descriptors.filter(
        (x) => x[field] == this.activity.id
      );
    },
    // Los títulos que ESTA pieza usa, y sólo ésos. El catálogo es de la institución, así
    // que pasarlo entero hacía que cada tarjeta dibujara un marcador vacío por cada título
    // creado en cualquier otra pieza —el contenedor recorre los títulos sin bloque y les
    // pinta el marco de "no se ve fuera de edición"—. Mismo criterio que en el Diseño.
    activityDescriptorTitles() {
      const usados = new Set(this.activityDescriptors.map((x) => x.descriptor));
      return this.descriptor_titles.filter(
        (x) =>
          x.page_position == this.PIECE_DESCRIPTOR_PAGE_POSITION &&
          usados.has(x.id)
      );
    },
    // El botón que lleva al instrumento nombra lo que uno va a abrir: la Actividad si la pieza
    // es del DI, o la Evaluación con el tipo que le da el Programa —Diagnóstica, Formativa,
    // Sumativa, Transversal— cuando no lo es. La Actividad no se nombra "Evaluación" aunque
    // por debajo lo sea: en el DI es otra cosa y así la llama el diseñador.
    goToEntityName() {
      if (this.isActivity) return this.activityEntityName;
      if (!this.cardTypeLabel) return this.evaluationEntityName;
      return `${this.evaluationEntityName} ${this.cardTypeLabel}`;
    },
    // Cómo se nombra la pieza en las acciones de la tarjeta: "Actividad" cuando es del DI, y
    // el tipo que le da el Programa —Diagnóstica, Formativa, Sumativa, Transversal— cuando no.
    // Así el tooltip dice sobre qué actúa, y no "Actividad" para todas.
    pieceEntityName() {
      return this.cardTypeLabel || this.activityEntityName;
    },
    cardTitle() {
      return (
        this.activity.name ||
        this.$t("teaching.instructional.activityUntitled", {
          entity: this.activityEntityName,
        })
      );
    },
    // E43 · Las divisiones de tiempo que esta asignatura cuenta, pedagógicas o cronológicas.
    // Mismo criterio que el presupuesto del diseño: el `counts_towards_credits` de la fila del
    // Programa. Se resuelve acá y no por props porque la tarjeta vive dos niveles abajo del
    // orquestador y ya conoce su asignatura.
    countedTimeAllocationIds() {
      const rows =
        this.level_key === "matter"
          ? this.matter_time_allocations.filter(
              (x) => x.matter == this.activity.matter
            )
          : this.ep_matter_time_allocations.filter(
              (x) => x.egress_profile_matter == this.activity.egress_profile_matter
            );
      return rows
        .filter((x) => x.counts_towards_credits == true)
        .map((x) => x.time_allocation);
    },
    // E37 · Las horas salen siempre del Programa: en el wrapper no son un campo, son la suma
    // de sus divisiones de tiempo. Desde el DI ya no se escriben.
    //
    // E43 · Se suman las mismas divisiones con las que se calculó su semana, no la posición 0
    // del par [pedagógicas, cronológicas]: mostrar un número y haber calculado con otro es
    // peor que no mostrarlo.
    cardHours() {
      const counted = this.countedTimeAllocationIds;
      if (counted.length === 0) return null;
      const key =
        this.level_key === "matter"
          ? "matter_evaluation"
          : "egress_profile_matter_evaluation";
      const hours = this.time_allocation_rows
        .filter(
          (x) => x[key] == this.activity.id && counted.includes(x.time_allocation)
        )
        .reduce((sum, x) => sum + Number(x.hours || 0), 0);
      if (!hours) return null;
      return parseFloat(hours.toFixed(2));
    },
    studentInstructions() {
      return this.activity.description_for_student || "";
    },
    // E50 · El resumen de la pieza, el mismo campo que el formulario del Programa edita.
    activityDescription() {
      return this.activity.description || "";
    },
    activityIndicators() {
      // E37 · Una sola fuente: los IL son del wrapper, sea del tipo que sea.
      const selected = this.activity.evaluation_criterias_micro || [];
      return this.evaluationCriteriasMicros
        .filter((x) => selected.includes(x.id))
        .sort((a, b) => a.order - b.order);
    },
    // Contenidos de la actividad, ordenados por la Unidad de Estudio de la que
    // provienen para que se lean en el orden del Programa.
    activityContents() {
      const selected = this.activity.contents || [];
      const study_unit_by_id = new Map(
        this.study_units.map((study_unit) => [study_unit.id, study_unit])
      );
      return this.study_unit_contents
        .filter((x) => selected.includes(x.id))
        .map((content) => {
          const study_unit = study_unit_by_id.get(content.study_unit);
          return {
            id: content.id,
            content: content.content,
            study_unit_order: study_unit ? study_unit.order : 0,
            study_unit_label: study_unit
              ? `${this.getCompetenceUnitOrder(
                  study_unit.temp_competence_unit
                )}${study_unit.order}`
              : "",
          };
        })
        .sort((a, b) => a.study_unit_order - b.study_unit_order);
    },
    // Bibliografía de la tarjeta: la que la ficha vinculó desde el DI más la que
    // la evaluación declara en el Programa. Son dos listas distintas — la segunda
    // no se edita aquí — pero se leen como una sola cosa: el material de apoyo de
    // lo que la actividad trabaja. Cada una se marca con su origen, y si un
    // recurso está en las dos aparece una vez.
    // E37 · Una sola lista. Antes eran dos —la que la ficha vinculaba y la que declaraba la
    // evaluación en el Programa (E25.1)—, y había que marcar cuál venía de dónde. Con una sola
    // fila el distintivo "Programa" deja de tener sentido: toda la bibliografía es del wrapper.
    activityBibliographicResources() {
      const selected = this.activity.bibliographic_resources || [];
      return this.bibliographic_resources
        .filter((x) => selected.includes(x.id))
        .sort((a, b) => a.type - b.type || a.id - b.id);
    },
    // La ponderación con la que la actividad entra en la nota final. Vive en el
    // wrapper del Programa en los dos casos (Actividad y evaluación referenciada),
    // así que se lee del mismo campo derivado. `null` cuando no pondera: una
    // Formativa vale 0 y no tiene sentido mostrar "0%".
    cardWeighing() {
      const weighing = Number(this.activity.weighing);
      if (isNaN(weighing) || weighing <= 0) return null;
      // FloatField: 30 y no 30.0, salvo que la institución use decimales.
      return parseFloat(weighing.toFixed(2));
    },
    // Los Recursos de Apoyo de esta actividad, agrupados por tipo. El dato llega
    // en la actividad como ids (derivados del wrapper del Programa) y se resuelve
    // contra el catálogo de la Asignatura.
    activitySupportResources() {
      const selected = this.activity.teaching_support_resources || [];
      const groups = [];
      this.teaching_support_resources
        .filter((x) => selected.includes(x.id))
        .forEach((resource) => {
          let group = groups.find((x) => x.type == resource.type);
          if (!group) {
            group = {
              type: resource.type,
              type_name: resource.type_name,
              items: [],
            };
            groups.push(group);
          }
          group.items.push(this.supportResourceContent(resource));
        });
      return groups;
    },
    // E30 · Los momentos del modelo que usa este diseño. Se resuelven desde el store en
    // vez de bajarlos por props: son cuatro niveles de árbol hasta acá.
    designMoments() {
      const design = this.instructional_designs.find(
        (x) => x.id == this.instructional_design_id
      );
      if (!design || !design.method) return [];
      const method = this.instructional_methods.find(
        (x) => x.id == design.method
      );
      if (!method) return [];
      return [...(method.moments || [])].sort((a, b) => a.order - b.order);
    },
    // Las horas autónomas con el mismo trato que las de aula: sin ceros de relleno.
    autonomousHours() {
      const number = Number(this.activity.autonomous_work_hours);
      if (isNaN(number)) return this.activity.autonomous_work_hours;
      return parseFloat(number.toFixed(2));
    },
    // Los momentos que la actividad activa, en el orden del modelo.
    activityMoments() {
      const selected = this.activity.moments || [];
      return this.designMoments.filter((x) => selected.includes(x.id));
    },
    // Los Ambientes de la ficha, resueltos contra el catálogo de la institución. El
    // dato llega derivado del wrapper del Programa (E29).
    activityStudyEnvironments() {
      const selected = this.activity.study_environments || [];
      return this.studyEnvironments
        .filter((x) => selected.includes(x.id))
        .sort((a, b) => a.name.localeCompare(b.name));
    },
    // Las Estrategias de la ficha, resueltas contra el catálogo de la institución.
    activityStrategies() {
      const selected = this.activity.methodological_strategies || [];
      return this.methodologicalStrategies
        .filter((x) => selected.includes(x.id))
        .sort((a, b) => a.name.localeCompare(b.name));
    },
    // E36 · Las Evidencias de la ficha, resueltas contra el catálogo de la institución.
    activityEvidences() {
      const selected = this.activity.procedure_evidences || [];
      return this.procedureEvidences
        .filter((x) => selected.includes(x.id))
        .sort((a, b) => a.name.localeCompare(b.name));
    },
    // E37 · El componente de bibliografía de EPM pide la Asignatura del Perfil de Egreso
    // entera, no sólo su id.
    egressProfileMatter() {
      if (this.level_key === "matter") return null;
      return (
        this.egress_profiles_matters.find(
          (x) => x.id == this.activity.egress_profile_matter
        ) || null
      );
    },
    activityWeek() {
      return this.activity_weeks.get(this.activity.id) || null;
    },
    // Una sola semana, o el rango que abarca si la actividad es larga.
    weekLabel() {
      if (!this.activityWeek || this.activityWeek.from == null) return null;
      const { from, to } = this.activityWeek;
      if (from == to)
        return this.$t("teaching.instructional.activityWeek", { week: from });
      return this.$t("teaching.instructional.activityWeekRange", {
        from: from,
        to: to,
      });
    },
    // Motivo por el que la actividad no cae en ninguna semana. Los cuatro casos
    // los distingue `activityWeeks` en Instructional.vue.
    weekMissingLabel() {
      if (!this.activityWeek || !this.activityWeek.reason) return null;
      // E40 · Se fue el motivo `no_container`: estar fuera de un Modulo dejó de impedir
      // el cálculo de la semana, así que ya no hay nada que explicar por ese lado.
      const labels = {
        no_hours: this.$t("teaching.instructional.activityWeekMissingNoHours"),
        chain_broken: this.$t(
          "teaching.instructional.activityWeekMissingChainBroken",
          { activity: this.activityEntityName }
        ),
        no_classroom_hours: this.$t(
          "teaching.instructional.activityWeekMissingNoClassroomHours"
        ),
      };
      return labels[this.activityWeek.reason] || null;
    },
    weekTooltip() {
      if (!this.activityWeek) return "";
      return this.activityWeek.overflow
        ? this.$t("teaching.instructional.activityWeekOverflow", {
            activity: this.activityEntityName,
            matter: this.matterEntityName,
          })
        : this.$t("teaching.instructional.activityWeekTooltip", {
            activities: this.piecesEntityNamePlural,
          });
    },
    orientation() {
      return this.activity.orientation || "";
    },
    orientationModalId() {
      return `instructional-activity-orientation-modal-${this.activity.id}`;
    },
    filesModalId() {
      return `instructional-activity-files-modal-${this.activity.id}`;
    },
    redoModalId() {
      return `instructional-activity-redo-modal-${this.activity.id}`;
    },
    // El Modulo que aloja la pieza, del store: es el Módulo del índice.
    activityModule() {
      if (!this.activity.module) return null;
      return (
        this.instructional_modules.find(
          (x) => x.id == this.activity.module
        ) || null
      );
    },
    // "M1 · Activación · Innovación digital...". Derivado, nunca guardado: el módulo lo da
    // el Modulo, la fase los momentos y la denominación el nombre de la pieza. Si falta
    // el Modulo se omite esa parte en vez de inventar un número de módulo.
    indexLabel() {
      const parts = [];
      if (this.activityModule)
        parts.push(
          this.$t("teaching.instructional.activityIndexModule", {
            order: this.activityModule.order,
          })
        );
      if (this.activityMoments.length > 0)
        parts.push(this.activityMoments.map((x) => x.name).join(" / "));
      if (this.activity.name) parts.push(this.activity.name);
      return parts.length > 1 ? parts.join(" · ") : null;
    },
    // Los adjuntos NO están en el wrapper: viven en la Evaluación de evaluations2, que es
    // donde se cargan al armar el instrumento. `activity.evaluation` es sólo su id, así que
    // hay que ir a buscar el objeto; una pieza a la que nunca se le armó instrumento no
    // tiene ninguno, y la sección no se dibuja.
    evaluationFiles() {
      if (!this.activity.evaluation) return [];
      const evaluation = this.new_evaluations.find(
        (x) => x.id == this.activity.evaluation
      );
      return (evaluation || {}).files || [];
    },
    participationLabel() {
      const labels = {
        1: this.$t("teaching.instructional.participationIndividual"),
        2: this.$t("teaching.instructional.participationPairs"),
        3: this.$t("teaching.instructional.participationGroup"),
      };
      return labels[this.activity.work_type] || null;
    },
  },
  watch: {
    // La Evaluación se pide al desplegar y no al montar: la vista dibuja decenas de
    // tarjetas de una vez y serían decenas de GET para algo que casi nadie abre. La
    // acción cachea, así que reabrir la misma tarjeta no vuelve a pedirla.
    expanded(value) {
      if (!value || !this.activity.evaluation) return;
      this.$store.dispatch(
        names.FETCH_NEW_EVALUATION,
        this.activity.evaluation
      );
    },
  },
  mounted() {
    if (this.highlight_activity_id != this.activity.id) return;
    this.expanded = true;
    this.highlighted = true;
    this.$nextTick(() => this.scrollWhenVisible(60));
    // El resalte es una señal de llegada, no un estado: se apaga solo para no
    // dejar la tarjeta marcada durante el resto de la sesión.
    this.highlight_timeout = setTimeout(() => {
      this.highlighted = false;
      this.highlight_timeout = null;
    }, 4000);
  },
  beforeDestroy() {
    if (this.highlight_timeout !== null) clearTimeout(this.highlight_timeout);
  },
  methods: {
    // Centrar la tarjeta al llegar por deep link desde el Programa.
    //
    // Antes alcanzaba con `nextTick`: la tarjeta se montaba recién cuando su Modulo se
    // abría, así que al montarse ya era visible. Ahora vive en el DOM desde el principio
    // —oculta— para poder imprimirse, y `scrollIntoView` sobre algo oculto no hace nada.
    // Se espera a que su Modulo la muestre, con un tope de intentos para no quedar
    // mirando un elemento que nadie va a abrir.
    scrollWhenVisible(intentos) {
      if (!this.$el || !this.$el.scrollIntoView) return;
      if (this.$el.offsetParent === null) {
        if (intentos <= 0) return;
        window.requestAnimationFrame(() => this.scrollWhenVisible(intentos - 1));
        return;
      }
      this.$el.scrollIntoView({ behavior: "smooth", block: "center" });
    },
    // El color del momento de fondo, con el texto aclarado u oscurecido según ese
    // color: mismo criterio que el resto de los chips con color de la vista.
    momentPillStyle(moment) {
      const color =
        moment.color && /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.test(moment.color)
          ? moment.color
          : "#2361a0";
      return {
        backgroundColor: color,
        color: this.$lightOrDark(color) ? "#2b2a2a" : "#ffffff",
        borderColor: color,
      };
    },
    // Prefijo de la Unidad de Competencia, cuando la institución las usa.
    getCompetenceUnitOrder(temp_competence_unit_id) {
      const competence_unit = this.competence_units.find(
        (x) => x.id == temp_competence_unit_id
      );
      return competence_unit ? competence_unit.order + "." : "";
    },
    // La descripcion de una estrategia viene como HTML y puede tener varios parrafos
    // con ejemplos y fuentes: en el tooltip va en texto plano y recortada, o seria una
    // pantalla entera flotando sobre la tarjeta.
    strategyTooltip(strategy) {
      const plain = this.$htmlToPlainText(strategy.description || "").trim();
      if (!plain) return strategy.name;
      if (plain.length <= 180) return plain;
      return `${plain.slice(0, 180).trim()}…`;
    },
    // Mismo recorte para la descripcion de una evidencia: explica que es el producto
    // ("Composicion escrita que expresa una reflexion profunda...") y puede ser larga.
    evidenceTooltip(evidence) {
      const plain = this.$htmlToPlainText(evidence.description || "").trim();
      if (!plain) return evidence.name;
      if (plain.length <= 180) return plain;
      return `${plain.slice(0, 180).trim()}…`;
    },
    indicatorPrefix(indicator) {
      const study_unit = this.study_units.find(
        (x) => x.id == indicator.study_unit
      );
      if (!study_unit) return "";
      return `${this.getCompetenceUnitOrder(study_unit.temp_competence_unit)}${
        study_unit.order
      }.`;
    },
    // ===== Descriptores de la pieza =====
    //
    // El título vive en el catálogo de la institución y el contenido en el through. Crear
    // un título nuevo, entonces, son dos pasos: primero el título y después su bloque
    // vacío en ESTA pieza —si no, el título existiría sin que se vea en ninguna parte.
    createActivityDescriptorTitle(title) {
      if (!isNaN(title.id)) return;
      this.$store
        .dispatch(names.POST_EGRESS_PROFILE_MATTER_DESCRIPTORS, {
          ...title,
          is_default: false,
        })
        .then((response) => this.createActivityDescriptor("", response))
        .catch(() => toast(this.$t("teaching.instructional.saveError")));
    },
    updateActivityDescriptorTitle(title) {
      if (isNaN(title.id)) return;
      this.$store.dispatch(names.PATCH_EGRESS_PROFILE_MATTER_DESCRIPTORS, {
        id: title.id,
        item: { title: title.title, is_default: false },
      });
    },
    createActivityDescriptor(text, title) {
      if (isNaN(title.id)) return;
      this.$store
        .dispatch(names.POST_INSTRUCTIONAL_ACTIVITY_DESCRIPTOR, {
          ...this.descriptorAnchor,
          descriptor: title.id,
          description: text,
          order: this.activityDescriptors.length + 1,
        })
        .catch(() => toast(this.$t("teaching.instructional.saveError")));
    },
    updateActivityDescriptor(descriptor, text) {
      if (isNaN(descriptor.id)) return;
      this.$store
        .dispatch(names.PATCH_INSTRUCTIONAL_ACTIVITY_DESCRIPTOR, {
          instructional_activity_descriptor_id: descriptor.id,
          item: { description: text },
        })
        .then(() => toast(this.$t("teaching.instructional.saved")))
        .catch(() => toast(this.$t("teaching.instructional.saveError")));
    },
    updateVisibleActivityDescriptor(descriptor) {
      if (isNaN(descriptor.id)) return;
      this.$store.dispatch(names.PATCH_INSTRUCTIONAL_ACTIVITY_DESCRIPTOR, {
        instructional_activity_descriptor_id: descriptor.id,
        item: { is_visible: !descriptor.is_visible },
      });
    },
    updateOrderActivityDescriptors(descriptors) {
      descriptors.forEach((descriptor) => {
        if (isNaN(descriptor.id)) return;
        if (
          this.activityDescriptors.findIndex((x) => x.id == descriptor.id) == -1
        )
          return;
        this.$store.dispatch(names.PATCH_INSTRUCTIONAL_ACTIVITY_DESCRIPTOR, {
          instructional_activity_descriptor_id: descriptor.id,
          item: { order: descriptor.order },
        });
      });
    },
    // Borra el bloque de ESTA pieza, no el título del catálogo: el título es de la
    // institución y puede estar en uso en otras piezas.
    deleteActivityDescriptor(descriptor) {
      this.$swal({
        title: this.$t("reusable.common.deleteConfirmTitle2", {
          entity: this.$t("teaching.instructional.descriptor"),
          article: "el",
        }),
        type: "warning",
        showCancelButton: true,
      }).then((result) => {
        if (!result.value) return;
        this.$store
          .dispatch(
            names.DELETE_INSTRUCTIONAL_ACTIVITY_DESCRIPTOR,
            descriptor.id
          )
          .then(() => this.deleteOrphanDescriptorTitle(descriptor.descriptor))
          .catch(() => toast(this.$t("teaching.instructional.saveError")));
      });
    },
    // Un título sin ningún bloque no se ve en ninguna parte —cada pieza muestra los
    // suyos—, así que quedaría para siempre en el catálogo sin que nadie pueda usarlo ni
    // borrarlo. Se pregunta al backend, y no al store, porque acá sólo están cargados los
    // bloques de ESTE diseño: una pieza de otra asignatura podría usar el mismo título.
    deleteOrphanDescriptorTitle(descriptor_title_id) {
      if (descriptor_title_id == null) return;
      this.$store
        .dispatch(names.FETCH_INSTRUCTIONAL_ACTIVITY_DESCRIPTORS, {
          descriptor: descriptor_title_id,
        })
        .then((rows) => {
          if ((rows || []).length > 0) return;
          this.$store.dispatch(
            names.DELETE_EGRESS_PROFILE_MATTER_DESCRIPTORS,
            descriptor_title_id
          );
        });
    },
    // E16: abre el armador de instrumentos de evaluations2 sobre la evaluación
    // real de la actividad. E37: es `activity.evaluation` directo — el wrapper apunta a ella
    // lo resuelve el backend, así que aquí no hay que mirar cuál de las 3 FK viene.
    redirectToInstrument() {
      if (!this.activity.evaluation) {
        toast(
          this.$t("teaching.instructional.activityInstrumentMissing", {
            evaluation: this.evaluationEntityName,
            activity: this.activityEntityName,
          })
        );
        return;
      }
      this.$router.push({
        name: "NewEvaluationView",
        params: {
          evaluation_id: this.activity.evaluation,
          instrument_id: null,
        },
      });
    },
    // E38 · El formulario no persiste: emite y guarda el padre, igual que en el Programa.
    //
    // El segundo argumento son los instrumentos. Antes se descartaba, así que declarar un Test
    // al editar desde el diseño no guardaba nada; y para poder colgarlo hace falta que el
    // wrapper tenga su `Evaluation`, que 9.073 piezas del tipo Actividad no tienen porque hasta
    // E16 un guard se las negaba. `ensureEvaluation` la crea la primera vez que se necesita.
    // El contenido de un Recurso de Apoyo, tal como se guardó: texto enriquecido.
    //
    // `name` es legacy y queda VACÍO en las filas nuevas —el modelo lo dice y el resto del
    // sistema sólo escribe y lee `description`—, así que leerlo dejaba la fila en blanco:
    // el recurso estaba vinculado a la pieza y la tarjeta no mostraba nada.
    supportResourceContent(resource) {
      return resource.description || resource.name || "";
    },
    // Vuelve a pedir ESTA pieza y la deja fresca en el store.
    //
    // La bibliografía del wrapper se escribe en la tabla intermedia desde el componente del
    // Programa, que no pasa por el store. La tarjeta lee `bibliographic_resources`, que el
    // serializer deriva de esa tabla, así que sin volver a pedir la pieza el recurso recien
    // vinculado no apareciía hasta que algo más guardara la evaluación —el formulario
    // completo, típicamente— y por eso parecía que la bibliografía la traía el otro campo.
    refreshPiece() {
      const url =
        this.level_key === "matter"
          ? `/teaching/matter-evaluation/${this.activity.id}/`
          : `/teaching/egress-profile-matter-evaluation/${this.activity.id}/`;
      const mutation =
        this.level_key === "matter"
          ? names.MUTATE_MATTER_EVALUATION
          : names.MUTATE_EGRESS_PROFILE_MATTER_EVALUATION;
      return this.$restful
        .Get(url)
        .then((response) => this.$store.commit(mutation, response))
        .catch(() => {});
    },
    async onEvaluationUpdated(evaluation, tests) {
      const action =
        this.level_key === "matter"
          ? names.UPDATE_MATTER_EVALUATION
          : names.UPDATE_EGRESS_PROFILE_MATTER_EVALUATION;
      try {
        const saved = await this.$store.dispatch(action, evaluation);
        if ((tests || []).length > 0) {
          const evaluation_id = await ensureEvaluation(this, {
            level_key: this.level_key,
            anchor_id:
              this.level_key === "matter"
                ? this.activity.matter
                : this.activity.egress_profile_matter,
            wrapper: saved || evaluation,
          });
          await saveTests(this, tests, evaluation_id, evaluation.name);
        }
        toast(this.$t("teaching.instructional.saved"));
        this.closeEditModal();
      } catch (error) {
        toast(this.$t("teaching.instructional.saveError"));
      }
    },
    // E45 · Cada apertura empieza limpia: ni el tema ni las casillas de la vez anterior
    // tienen por qué volver. Marcar es siempre una decisión explícita.
    openRedo() {
      this.redo_theme = "";
      this.redo_parts = [];
      this.$bvModal.show(this.redoModalId);
    },
    /**
     * Regenera esta pieza con IA.
     *
     * Qué se reemplaza y qué se conserva lo decide `redoPieceWithAi`, que es la misma
     * función que usa el modal de creación rápida. Acá sólo se junta lo que el usuario
     * eligió y se le avisa cómo salió.
     *
     * No hay confirmación aparte: el modal con las casillas ES la confirmación, y es más
     * informativa que un "¿seguro?" porque dice exactamente qué se va a tocar.
     */
    async redoWithAi() {
      if (this.redoing || this.redo_parts.length === 0) return;
      this.redoing = true;
      try {
        const fresca = await redoPieceWithAi(this, {
          level_key: this.level_key,
          egress_profile_matter_id: this.egress_profile_matter_id,
          // E52 · Entrando por la Asignatura base no hay plan: el nivel se identifica
          // con la Asignatura base, que la pieza trae en su propio `matter`.
          matter_base_id: this.egress_profile_matter_id
            ? null
            : this.activity.matter,
          piece_id: this.activity.id,
          theme: this.redo_theme,
          parts: this.redo_parts,
        });
        if (!fresca) {
          toast(this.$t("reusable.aiInstructionalBuilderModal.emptyResult"));
          return;
        }
        toast(this.$t("reusable.aiInstructionalBuilderModal.redone"));
        this.$bvModal.hide(this.redoModalId);
      } catch (error) {
        toast(
          error.message ||
            this.$t("reusable.aiInstructionalBuilderModal.generateError")
        );
      } finally {
        this.redoing = false;
      }
    },
    // Adjuntar desde el diseño. El archivo se guarda en la Evaluación, que es donde vive el
    // campo, así que lo que se sube acá es lo mismo que después se ve en la evaluación: no
    // hay copia ni sincronización. Una pieza sin `Evaluation` la crea en el momento
    // —`ensureEvaluation`—, que es lo mismo que ya hace declarar un instrumento.
    async attachFile(file) {
      this.$bvModal.hide(this.filesModalId);
      if (!file) return;
      try {
        const evaluation_id = await ensureEvaluation(this, {
          level_key: this.level_key,
          anchor_id:
            this.level_key === "matter"
              ? this.activity.matter
              : this.activity.egress_profile_matter,
          wrapper: this.activity,
        });
        // Se relee antes de concatenar: si la Evaluación recién se creó, la copia del store
        // puede no tener todavía su lista de archivos, y un PATCH con la lista corta borra
        // los que ya estaban.
        const evaluation = await this.$store.dispatch(
          names.FETCH_NEW_EVALUATION,
          { evaluation_id: evaluation_id, forceFetch: true }
        );
        await this.$store.dispatch(names.PATCH_NEW_EVALUATION, {
          new_evaluation_id: evaluation_id,
          item: { files: (evaluation.files || []).concat([file.id]) },
        });
        toast(this.$t("teaching.instructional.saved"));
      } catch (error) {
        toast(this.$t("teaching.instructional.saveError"));
      }
    },
    // La Orientación se persiste sola, sin pasar por el formulario del Programa: es un campo
    // de la capa del diseño y se edita leyendo la pieza, no rellenando una ficha.
    async saveOrientation(content) {
      try {
        await patchEvaluation(this, this.level_key, this.activity.id, {
          orientation: content,
        });
        toast(this.$t("teaching.instructional.saved"));
      } catch (error) {
        toast(this.$t("teaching.instructional.saveError"));
      }
    },
    closeEditModal() {
      this.$bvModal.hide(
        `instructional-activity-edit-modal-${this.activity.id}`
      );
    },
    // El backend borra con la actividad la evaluación de SU nivel (E16): en
    // Matter/EPM la plantilla, en Sección la instancia real. Se avisa antes,
    // porque en Sección se van también las respuestas de los estudiantes.
    deleteWarning() {
      if (!this.activity.evaluation) return null;
      return this.$t("teaching.instructional.activityDeleteEvaluationWarning", {
        evaluation: this.evaluationEntityName,
        evaluations: this.evaluationEntityNamePlural,
        activity: this.activityEntityName,
      });
    },
    askDelete() {
      this.$swal({
        title: this.$t("reusable.common.deleteConfirmTitle2", {
          entity: this.activityEntityName,
          article: "la",
        }),
        text: this.deleteWarning(),
        type: "warning",
        showCancelButton: true,
      }).then((result) => {
        if (!result.value) return;
        // E37 · Se borra la evaluación del Programa: es la misma fila. Por eso el aviso
        // advierte que se va de las dos vistas, no sólo del diseño.
        const action =
          this.level_key === "matter"
            ? names.DELETE_MATTER_EVALUATION
            : names.DELETE_EGRESS_PROFILE_MATTER_EVALUATION;
        this.$store.dispatch(action, this.activity.id).catch(() => {
          toast(this.$t("teaching.instructional.saveError"));
        });
      });
    },
  },
};
</script>

<style scoped>
/* El asa del arrastre va `position: absolute; left: 20px` dentro de la caja del Descriptor,
   así que no ocupa sitio en la fila del título. Al ancho del Programa el título queda
   centrado lejos de ella; al ancho de esta tarjeta le cae encima. Se le reserva el hueco a
   los dos lados —para que siga centrado— y se centra el texto, que es como se lee en el
   Programa. El contenedor es compartido, así que se ajusta desde afuera. */
.kl-act-descriptors ::v-deep .descriptor-title > strong {
  padding-inline: 3rem;
  text-align: center;
}
/* El botón "Agregar Descriptor" lo pone el contenedor en su último div, alineado a la
   izquierda. Acá va centrado, como el resto de las acciones de la tarjeta; el contenedor
   es compartido con el Programa y el Diseño, así que se ajusta desde afuera. */
.kl-act-descriptors ::v-deep > div > div:last-child {
  text-align: center;
}
.kl-activity {
  background-color: #fff;
  border: 1px solid var(--kl-table-border, #d9e0e0);
  border-radius: var(--kl-radius-md, 8px);
  margin-bottom: 0.55rem;
  overflow: hidden;
}
/* Llegada por deep link desde el Programa. El resalte se apaga solo a los 4s
   (ver `mounted`), por eso es una señal y no un estado permanente. */
.kl-activity.highlighted {
  border-color: var(--kl-color-primary, #006ec8);
  box-shadow: 0 0 0 3px rgba(0, 110, 200, 0.18);
  animation: kl-activity-arrive 0.6s ease-out;
}
@keyframes kl-activity-arrive {
  from {
    box-shadow: 0 0 0 8px rgba(0, 110, 200, 0);
  }
  to {
    box-shadow: 0 0 0 3px rgba(0, 110, 200, 0.18);
  }
}
.kl-act-head {
  display: flex;
  align-items: flex-start;
  gap: 0.6rem;
  padding: 0.65rem 0.8rem;
  cursor: pointer;
  user-select: none;
}
.kl-act-head:hover {
  background-color: #f7fbfa;
}
.kl-act-handle {
  color: #97a5a3;
  margin-top: 0.1rem;
  flex: none;
  cursor: grab;
}
.kl-act-handle:active {
  cursor: grabbing;
}
.kl-act-handle:hover {
  color: var(--kl-color-primary, #006ec8);
}
.kl-caret {
  color: #6b7b79;
  margin-top: 0.15rem;
  transition: transform 0.15s;
}
.kl-activity.open .kl-caret {
  transform: rotate(90deg);
}
.kl-act-card {
  flex: 1;
  min-width: 0;
}
.kl-act-top {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
}
.kl-act-title {
  font-weight: var(--kl-fw-bold, 700);
  color: var(--kl-color-primary, #006ec8);
  font-size: var(--kl-fs-title-sm);
  text-align: left;
  /* Mismo motivo que en el Módulo: con base `auto`, un nombre largo se iba entero al
     renglón de abajo y dejaba solos a los chips que lo acompañan. */
  flex: 1 1 0;
}
.kl-act-units {
  display: flex;
  align-items: center;
  gap: 0.2rem;
  flex-wrap: wrap;
  justify-content: flex-end;
}
.kl-act-meta {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.35rem 0.9rem;
  margin-top: 0.55rem;
}
/* Cada dato es un bloque; la divisoria sólo aparece entre bloques. */
.kl-meta-group {
  display: flex;
  align-items: center;
  gap: 0.3rem;
  flex-wrap: wrap;
  color: #6b7b79;
}
.kl-meta-group + .kl-meta-group {
  border-left: 1px solid var(--kl-table-border, #d9e0e0);
  padding-left: 0.85rem;
}
.kl-meta-label {
  font-size: var(--kl-fs-caption);
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #97a5a3;
  font-weight: var(--kl-fw-bold, 700);
}
.kl-meta-value {
  font-size: var(--kl-fs-caption);
  color: #4a5c5a;
  font-weight: var(--kl-fw-bold, 600);
}
/* El diseño se pasó de las semanas de la asignatura: es un aviso, no un dato. */
.kl-week-overflow {
  color: #a12d2d;
}
.kl-meta-empty {
  font-size: var(--kl-fs-caption);
  color: #97a5a3;
  font-style: italic;
}
.kl-chip {
  display: inline-block;
  background-color: var(--kl-chip-bg, #e0f1ff);
  color: var(--kl-chip-text, #006ec8);
  border: 1px solid var(--kl-chip-border, #b6d9f5);
  border-radius: var(--kl-radius-pill, 999px);
  padding: var(--kl-chip-padding, 2px 8px);
  font-size: var(--kl-chip-fs);
  font-weight: var(--kl-chip-fw, 600);
}
.kl-act-body {
  text-align: left;
  border-top: 1px solid var(--kl-table-border, #d9e0e0);
  padding: 0.75rem 0.9rem;
  font-size: var(--kl-fs-body);
}
.kl-act-subtitle {
  font-size: var(--kl-fs-caption);
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #6b7b79;
  font-weight: var(--kl-fw-bold, 700);
  margin-bottom: 0.35rem;
}
.kl-il-list {
  margin: 0;
  font-size: var(--kl-fs-body);
}
/* El contenido llega como HTML del Programa: se alinea junto a su RA. */
/* Distintivo del tipo de una evaluacion referenciada, antes del nombre. */
.kl-chip-eval {
  background-color: var(--kl-chip-bg, #eef2f7);
  color: var(--kl-chip-text, #45596b);
  text-transform: uppercase;
  font-size: var(--kl-fs-caption);
  letter-spacing: 0.02em;
  margin-right: 0.35rem;
}
.kl-act-origin {
  font-size: var(--kl-fs-caption);
  color: var(--kl-text-muted, #6c757d);
  font-style: italic;
  margin-bottom: 0.5rem;
}
.kl-content-row {
  display: flex;
  align-items: baseline;
  gap: 0.4rem;
  font-size: var(--kl-fs-body);
}
/* Las dos columnas del cuerpo. La izquierda se lleva el ancho porque es texto de
   lectura; la derecha son listas cortas. */
.kl-act-panels {
  display: grid;
  grid-template-columns: minmax(0, 3fr) minmax(0, 2fr);
  gap: 1.25rem 2rem;
}
/* Debajo de ~900px la columna de la derecha ya no tiene ancho util: se apila y
   pierde la divisoria, que ahi separaria de arriba y no de al lado. */
@media (max-width: 900px) {
  .kl-act-panels {
    grid-template-columns: 1fr;
  }
  .kl-act-side {
    border-left: 0;
    padding-left: 0;
    border-top: 1px solid var(--kl-table-border, #d9e0e0);
    padding-top: 0.9rem;
  }
}
.kl-act-main,
.kl-act-side {
  min-width: 0;
}
.kl-act-side {
  border-left: 1px solid var(--kl-table-border, #d9e0e0);
  padding-left: 1.5rem;
}
.kl-block + .kl-block {
  margin-top: 0.9rem;
}
.kl-side-row {
  font-size: var(--kl-fs-body);
  color: #000;
}
/* La fila de momentos va entre el titulo y los metadatos: es lo primero que se lee
   despues del nombre, y por eso reemplaza a la caja de color que la contenia. */
.kl-moment-pill {
  margin-right: 0.2rem;
  display: inline-flex;
  align-items: center;
  gap: 0.15rem;
  border: 1px solid transparent;
  border-radius: var(--kl-radius-pill, 999px);
  padding: 1px 8px;
  font-size: var(--kl-fs-caption);
  font-weight: var(--kl-chip-fw, 600);
}
/* Los chips de la columna derecha fluyen sueltos, sin modulo flex. */
.kl-act-side .kl-chip {
  margin: 0.1rem 0.2rem 0.1rem 0;
}
/* El tipo hace de etiqueta de la fila, como el rotulo del grupo en el selector. */
.kl-support-type {
  color: #000;
  flex-shrink: 0;
}
/* El contenido del recurso tiene que leerse en la MISMA linea que su tipo, y viene de
   RenderVHtml, que monta un div con su propio padding y parrafos de bloque: aca se aplana
   para que la fila siga siendo una fila. El componente es compartido, asi que se ajusta
   desde afuera y no se le toca el estilo propio. */
.kl-support-item,
.kl-support-item ::v-deep .rich-text-content,
.kl-support-item ::v-deep p {
  display: inline;
}
.kl-support-item ::v-deep .rich-text-content,
.kl-support-item ::v-deep p {
  padding: 0;
  margin: 0;
}
/* Bibliografia que viene declarada en el Programa: se refleja, no se edita. */
.kl-chip-origin {
  background-color: var(--kl-chip-bg, #eef2f7);
  color: var(--kl-chip-text, #000);
  text-transform: uppercase;
  font-size: var(--kl-fs-caption);
  letter-spacing: 0.02em;
  flex-shrink: 0;
  align-self: center;
}
/* E41 · La Orientación cruza las dos columnas: separada por una línea arriba porque cierra
   la tarjeta, no porque pertenezca a ninguna de las dos. */
.kl-act-orientation {
  margin-top: 1.1rem;
  padding-top: 0.9rem;
  border-top: 1px solid var(--kl-table-border, #d9e0e0);
}
.kl-act-index {
  font-size: var(--kl-fs-caption);
  color: #6b7b79;
  font-weight: var(--kl-fw-bold, 600);
  margin-bottom: 0.35rem;
}
/* Editable al clic, como la descripción del Modulo. */
.kl-orientation-box {
  cursor: pointer;
  border-radius: var(--kl-radius-md, 8px);
  padding: 0.25rem 0.4rem;
  margin: -0.25rem -0.4rem;
}
.kl-orientation-box:hover {
  background-color: #f7fbfa;
}
/* Los "sin nada" de la tarjeta: mismo texto, mismo tamaño. Estaban a dos medidas
   distintas según qué sección los mostrara. */
.kl-act-empty {
  font-size: var(--kl-fs-body);
  color: #97a5a3;
  font-style: italic;
}
/* La ponderacion es el dato que mas se busca de un vistazo: se destaca. */
.kl-weighing-value {
  color: var(--kl-color-primary, #006ec8);
}

/* En papel no hay quien haga clic: el diseño se imprime entero y sin los adornos que
   sólo sirven para operarlo en pantalla. */
@media print {
  .kl-act-body {
    display: block !important;
  }
  /* La hoja es angosta y las dos columnas se apilan; la divisoria vertical quedaba
     cortando por el medio un bloque que ya no está al lado de nada. */
  .kl-act-panels {
    grid-template-columns: 1fr;
    gap: 0.9rem;
  }
  .kl-act-side {
    border-left: 0;
    padding-left: 0;
  }
  /* El triángulo indica que la tarjeta se abre y se cierra: impresa ya está abierta. */
  .kl-caret,
  .kl-act-handle {
    display: none;
  }
  .kl-activity {
    border-radius: 0;
    /* Una tarjeta partida entre dos hojas se lee dos veces para entenderla una. */
    break-inside: avoid;
    page-break-inside: avoid;
  }
  .kl-activity.highlighted {
    border-color: var(--kl-table-border, #d9e0e0);
    box-shadow: none;
    animation: none;
  }
  .kl-act-head {
    cursor: auto;
    padding-bottom: 0.35rem;
  }
}
</style>
